/**
 * FmContextPanelContext — per-view slot contract for the icon rail + contextual panel.
 *
 * Pages register panels via <FmContextPanelSlot panels={[...]} />.
 * FmIconRail reads panels to render icon buttons.
 * FmContextPanel reads activePanel to render content.
 * Open/closed state persisted via UiPreferencesStore (localStorage seam).
 *
 * The context object, the `useFmContextPanel` hook, and the PanelEntry type live in
 * ./fmContextPanelInternals so this file exports only React components (react-refresh).
 */
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useOptionalAuth } from "@/contexts/useOptionalAuth";
import {
  createLocalStorageUiPreferences,
  type UiPreferences,
  type UiPreferencesStore,
} from "@/stores/uiPreferences";
import {
  FmContextPanelContext,
  type FmContextPanelContextValue,
  type FmMapToolsPlacement,
  type PanelEntry,
} from "@/contexts/fmContextPanelInternals";

// Re-export the type so existing `import { PanelEntry } from '.../FmContextPanelContext'`
// call sites continue to resolve (types are erased at runtime — no fast-refresh impact).
export type { PanelEntry } from "@/contexts/fmContextPanelInternals";

export const FmContextPanelProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const auth = useOptionalAuth();

  const prefsStore = useMemo<UiPreferencesStore>(
    () => createLocalStorageUiPreferences(auth?.user?.email ?? null),
    [auth?.user?.email],
  );

  const [panels, setPanels] = useState<PanelEntry[]>([]);
  const [mapToolsPlacement, setMapToolsPlacement] =
    useState<FmMapToolsPlacement>("shell");
  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(
    () => prefsStore.get().iconRailPanelOpen,
  );
  const [isPanelPinned, setIsPanelPinned] = useState<boolean>(
    () => prefsStore.get().iconRailPanelOpen,
  );
  const isPanelPinnedRef = useRef(isPanelPinned);
  const [activePanel, setActivePanel] = useState<string | null>(
    () => prefsStore.get().iconRailActivePanel,
  );
  const peekCloseTimer = useRef<number | null>(null);

  const cancelPeekClose = useCallback(() => {
    if (peekCloseTimer.current === null) return;
    window.clearTimeout(peekCloseTimer.current);
    peekCloseTimer.current = null;
  }, []);

  // Sync open/active from the resolved store (handles anon → real-user prefs namespace switch)
  useEffect(() => {
    const p = prefsStore.get();
    setIsPanelOpen(p.iconRailPanelOpen);
    setIsPanelPinned(p.iconRailPanelOpen);
    isPanelPinnedRef.current = p.iconRailPanelOpen;
    setActivePanel(p.iconRailActivePanel);
  }, [prefsStore]);

  const persistPrefs = useCallback(
    (patch: Partial<UiPreferences>) => {
      prefsStore.set(patch);
    },
    [prefsStore],
  );

  const openPanel = useCallback(
    (key: string) => {
      cancelPeekClose();
      setActivePanel(key);
      setIsPanelOpen(true);
      setIsPanelPinned(true);
      isPanelPinnedRef.current = true;
      persistPrefs({ iconRailActivePanel: key, iconRailPanelOpen: true });
    },
    [cancelPeekClose, persistPrefs],
  );

  const closePanel = useCallback(() => {
    cancelPeekClose();
    setIsPanelOpen(false);
    setIsPanelPinned(false);
    isPanelPinnedRef.current = false;
    setActivePanel(null);
    persistPrefs({ iconRailPanelOpen: false, iconRailActivePanel: null });
  }, [cancelPeekClose, persistPrefs]);

  const peekPanel = useCallback(
    (key: string) => {
      if (isPanelPinnedRef.current) return;
      cancelPeekClose();
      setActivePanel(key);
      setIsPanelOpen(true);
    },
    [cancelPeekClose],
  );

  const scheduleClosePeekedPanel = useCallback(() => {
    cancelPeekClose();
    if (isPanelPinnedRef.current) return;
    peekCloseTimer.current = window.setTimeout(() => {
      if (isPanelPinnedRef.current) {
        peekCloseTimer.current = null;
        return;
      }
      setIsPanelOpen(false);
      setActivePanel(null);
      peekCloseTimer.current = null;
    }, 160);
  }, [cancelPeekClose]);

  useEffect(() => cancelPeekClose, [cancelPeekClose]);

  const togglePanel = useCallback(
    (key: string) => {
      if (isPanelOpen && isPanelPinned && activePanel === key) {
        closePanel();
      } else {
        openPanel(key);
      }
    },
    [isPanelOpen, isPanelPinned, activePanel, closePanel, openPanel],
  );

  // Visual-only close: does NOT persist to localStorage.
  // Used for cross-route stale panel cleanup to avoid overwriting the user's stored open-state.
  const closeVisualOnly = useCallback(() => {
    setIsPanelOpen(false);
    setIsPanelPinned(false);
    isPanelPinnedRef.current = false;
    setActivePanel(null);
  }, []);

  // Auto-close stale activePanel when navigating between views (visual only — no persist).
  useLayoutEffect(() => {
    if (
      activePanel !== null &&
      panels.length > 0 &&
      !panels.some((p) => p.key === activePanel)
    ) {
      closeVisualOnly();
    }
  }, [panels, activePanel, closeVisualOnly]);

  // A route with no panel slot must not inherit a blank open panel from the
  // previous route. Defer one frame so StrictMode effect replay and a newly
  // mounting slot can re-register before the visual-only close runs.
  useEffect(() => {
    if (activePanel === null || panels.length > 0) return undefined;
    const frame = window.requestAnimationFrame(closeVisualOnly);
    return () => window.cancelAnimationFrame(frame);
  }, [activePanel, panels.length, closeVisualOnly]);

  const value = useMemo<FmContextPanelContextValue>(
    () => ({
      panels,
      setPanels,
      mapToolsPlacement,
      setMapToolsPlacement,
      activePanel,
      isPanelOpen,
      isPanelPinned,
      openPanel,
      peekPanel,
      scheduleClosePeekedPanel,
      cancelPeekClose,
      closePanel,
      togglePanel,
    }),
    [
      panels,
      mapToolsPlacement,
      activePanel,
      isPanelOpen,
      isPanelPinned,
      openPanel,
      peekPanel,
      scheduleClosePeekedPanel,
      cancelPeekClose,
      closePanel,
      togglePanel,
    ],
  );

  return (
    <FmContextPanelContext.Provider value={value}>
      {children}
    </FmContextPanelContext.Provider>
  );
};

/**
 * panelsStructurallyEqual — hybrid check for panel list equality.
 *
 * Fast path: if arrays have the same identity (===), they're unchanged → true.
 * This handles memoized callers where the reference is preserved across renders.
 *
 * Deep check: if arrays are different references, compare structurally:
 *   - Array length and order
 *   - Per-entry: key (value), tooltip (value), icon (reference ===), content (reference ===)
 *
 * This detects:
 *   ✓ Real changes: language update (tooltip changes) or data update (content ref changes)
 *   ✓ Avoids loops: spurious re-render with same tooltip+content refs → no update
 *   ✓ Prevents stale: new icon/content refs (real update) → detected and propagated
 *
 * Callers MUST memoize their panels so icon/content refs remain stable when nothing changed.
 * MapView: stable icons (module level), memoized content (useMemo per component).
 * Other callers: apply useMemo to ensure refs are stable across re-renders when data unchanged.
 */
function panelsStructurallyEqual(
  oldPanels: PanelEntry[],
  newPanels: PanelEntry[],
): boolean {
  // Fast path: same array reference means same panels (memoized caller).
  if (oldPanels === newPanels) return true;

  // Different references: check structure.
  if (oldPanels.length !== newPanels.length) return false;

  return oldPanels.every((oldPanel, i) => {
    const newPanel = newPanels[i];
    // Compare stable identifiers: key (value), tooltip (value).
    // Compare reference-based properties: icon (===), content (===).
    // If any entry differs, the panel list changed.
    return (
      oldPanel.key === newPanel.key &&
      oldPanel.tooltip === newPanel.tooltip &&
      oldPanel.visibleInMapTools === newPanel.visibleInMapTools &&
      oldPanel.icon === newPanel.icon &&
      oldPanel.content === newPanel.content
    );
  });
}

/**
 * FmContextPanelSlot — mount in a page view to register view-scoped panels.
 * Cleans up on unmount (navigating away clears the view's panels).
 *
 * Idempotent registration with hybrid guard: only calls setPanels when panels
 * genuinely change. This prevents render loops from spurious re-renders while
 * detecting real content/icon updates and language changes.
 *
 * The guard uses:
 *   1. Fast path (array identity) for memoized callers (ref unchanged → no update)
 *   2. Deep structural check (key+tooltip value, icon+content ref) to detect real changes
 *
 * Callers should memoize their panels (useMemo the array, stable icons, memoized content)
 * so icon/content refs are preserved when nothing changed. This ensures the guard's
 * reference-equality checks work correctly.
 *
 * Usage (with proper memoization):
 *   const panels = useMemo(() => [
 *     { key: 'layers', icon: <AppstoreOutlined />, tooltip: 'Map layers', content: <LayersPanel /> },
 *   ], [tooltip, content]);
 *   <FmContextPanelSlot panels={panels} />
 */
export const FmContextPanelSlot: React.FC<{
  panels: PanelEntry[];
  mapToolsPlacement?: FmMapToolsPlacement;
}> = ({ panels: panelsProp, mapToolsPlacement = "shell" }) => {
  // Use context directly so this component is a no-op in layouts that have no provider
  // FmContextPanelProvider wraps the outlet in FmShellLayout (mywox.3); no provider = no-op.
  const ctx = useContext(FmContextPanelContext);

  const setPanels = ctx?.setPanels;
  const setMapToolsPlacement = ctx?.setMapToolsPlacement;

  // Track previously registered panels to detect genuine changes.
  // Prevents spurious setPanels calls when array identity changes but structural content
  // (keys, tooltips, icon refs, content refs) is identical.
  const prevPanelsRef = useRef<PanelEntry[]>([]);

  // Register (and re-register) only when panels are structurally different.
  // Hybrid guard: fast-path on array identity (memoized callers), else deep check on
  // key+tooltip (value) and icon+content (reference). Only call setPanels when something
  // genuinely changed (language, data, structure).
  useLayoutEffect(() => {
    if (!setPanels) return; // No provider — panel registration is a no-op
    if (!panelsStructurallyEqual(prevPanelsRef.current, panelsProp)) {
      setPanels(panelsProp);
      prevPanelsRef.current = panelsProp;
    }
  }, [setPanels, panelsProp]);

  useLayoutEffect(() => {
    if (!setMapToolsPlacement) return;
    setMapToolsPlacement(mapToolsPlacement);
  }, [setMapToolsPlacement, mapToolsPlacement]);

  // Clear the view's panels on unmount only (navigating away).
  useLayoutEffect(() => {
    if (!setPanels) return;
    return () => {
      prevPanelsRef.current = [];
      setPanels([]);
      setMapToolsPlacement?.("shell");
    };
  }, [setPanels, setMapToolsPlacement]);

  return null;
};
